// Load optional Module Federation remotes on demand instead of wiring them into initial shell startup.
/* global __webpack_init_sharing__, __webpack_share_scopes__ */
const remoteScriptPromises = new Map();
const initializedShareScopes = new Set();

function getRemoteScriptId(scope) {
  return `remote-script-${scope}`;
}

function loadRemoteScript(scope, remoteEntryUrl) {
  if (!remoteEntryUrl) {
    return Promise.reject(new Error(`Missing remoteEntry URL for ${scope}`));
  }

  if (window[scope]) {
    return Promise.resolve(window[scope]);
  }

  if (remoteScriptPromises.has(scope)) {
    return remoteScriptPromises.get(scope);
  }

  const scriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(getRemoteScriptId(scope));

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window[scope]), { once: true });
      existingScript.addEventListener('error', () => reject(new Error(`Failed to load ${remoteEntryUrl}`)), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = getRemoteScriptId(scope);
    script.src = remoteEntryUrl;
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      if (!window[scope]) {
        reject(new Error(`Remote container ${scope} was not attached to window`));
        return;
      }
      resolve(window[scope]);
    };

    script.onerror = () => {
      remoteScriptPromises.delete(scope);
      script.remove();
      reject(new Error(`Failed to load ${remoteEntryUrl}`));
    };

    document.head.appendChild(script);
  });

  remoteScriptPromises.set(scope, scriptPromise);
  return scriptPromise;
}

async function initializeShareScope(scope, container) {
  if (initializedShareScopes.has(scope)) {
    return;
  }

  if (typeof __webpack_init_sharing__ !== 'function' || !__webpack_share_scopes__?.default) {
    return;
  }

  await __webpack_init_sharing__('default');

  try {
    await container.init(__webpack_share_scopes__.default);
  } catch (error) {
    if (!/already initialized/i.test(String(error?.message || ''))) {
      throw error;
    }
  }

  initializedShareScopes.add(scope);
}

export async function loadRemoteModule({ scope, remoteBaseUrl, exposedModule, initializeSharing = true }) {
  const remoteEntryUrl = `${remoteBaseUrl.replace(/\/$/, '')}/remoteEntry.js`;
  const container = await loadRemoteScript(scope, remoteEntryUrl);

  if (!container || typeof container.get !== 'function') {
    throw new Error(`Remote container ${scope} is invalid`);
  }

  if (initializeSharing) {
    await initializeShareScope(scope, container);
  }

  const factory = await container.get(exposedModule);
  return factory();
}